import posthog from "posthog-js";
import type {CaptureResult} from "posthog-js";
import {authenticationApiService} from "./authentication/authenticationApi.service.js";
import {envService} from "./services/env.service.js";

/**
 * PostHog project API keys (public, same exposure model as the Sentry DSN in
 * sentry.ts). Provisioned in #468 — PostHog Cloud EU, separate prod and dev
 * projects. An empty key keeps PostHog disabled for that environment (init is
 * a no-op).
 *
 * Every environment now initializes PostHog so feature flags can be evaluated
 * everywhere, but capture (events + replay) stays scoped to prod/dev — see
 * `isCaptureMode`. The project split routes each environment to a project:
 *   - prod + canary  → prod project,
 *   - dev  + latest  → dev project.
 * Canary mirrors prod (1 user) so it rehearses the real prod flag targeting;
 * latest carries throwaway test accounts so it stays on the dev project.
 */
const POSTHOG_PROD_KEY = "phc_CXzCFRaFXHszuhNYtPyTubugCxfGyWa3QiMiW8EAna2G";
const POSTHOG_DEV_KEY = "phc_kGfb54PPrPi74dhN9C9s8E2AQ56deBs5zdQpYR8LQctE";
const POSTHOG_HOST = "https://eu.i.posthog.com";

let initialized = false;
let captureEnabled = false;

/** Retire, en place, toute clé de `bucket` qui ressemble à une URL/un referrer. */
function deleteUrlLikeKeys(bucket: Record<string, unknown> | null | undefined): void {
  if (!bucket) {
    return;
  }
  for (const key of Object.keys(bucket)) {
    if (/url|pathname|referr/i.test(key)) {
      delete bucket[key];
    }
  }
}

/**
 * Retire toute propriété liée à l'URL/au referrer d'un event PostHog avant
 * envoi (#920) : `autocapture`/`capture_pageview` désactivés ci-dessous ne
 * protègent QUE les triggers automatiques — un `posthog.capture()` explicite
 * (tout `analyticsService.track`) reçoit quand même ces propriétés par
 * défaut du SDK (`$current_url`/`$pathname`, les variantes `$session_entry_*`
 * calculées une fois par session, ET les propriétés d'identité "au premier
 * contact" — `$initial_current_url`/`$initial_referrer` — que PostHog place
 * dans `event.$set_once`, un champ SŒUR de `event.properties`, pas imbriqué
 * dedans). Une route qui porte un identifiant privé (ex. `/customer/:id`)
 * fuiterait donc cet id vers PostHog sur n'importe quel event métier tiré
 * depuis cette page (#920). Motif plutôt qu'une liste de clés : un
 * `property_denylist` statique avait laissé passer `$session_entry_url`,
 * absent de la première liste écrite.
 */
export function stripUrlProperties(event: CaptureResult | null): CaptureResult | null {
  if (!event) {
    return event;
  }
  deleteUrlLikeKeys(event.properties);
  deleteUrlLikeKeys(event.$set);
  deleteUrlLikeKeys(event.$set_once);
  return event;
}

export function initAnalytics(
  // Injectable for tests only — lets a test exercise the enabled path with
  // throwaway keys rather than depending on the real constants.
  keys: {prod: string; dev: string} = {prod: POSTHOG_PROD_KEY, dev: POSTHOG_DEV_KEY}
) {
  const mode = envService.getMode();
  const key = resolvePosthogKey(mode, keys);
  if (!key) {
    return;
  }
  posthog.init(key, {
    api_host: POSTHOG_HOST,
    // Explicit events only — autocapture would hoover up form values (PII).
    autocapture: false,
    // The funnel is driven by explicit events; pageviews would only add volume.
    capture_pageview: false,
    capture_pageleave: false,
    before_send: stripUrlProperties,
    /**
     * `before_send` (ci-dessus) ne protège que le pipeline `capture()` — le
     * SDK envoie aussi `person_properties` (dont `$initial_current_url`/
     * `$initial_pathname`, calculés une fois par `set_initial_person_info`)
     * dans le corps de `POST /flags`, un chemin ENTIÈREMENT distinct qui ne
     * passe jamais par `before_send` : colmater `$set_once` seul ne suffit
     * pas, la même valeur ressort par ce second chemin (#920). Désactiver la
     * collecte à la source retire les deux fuites d'un coup, plutôt que
     * d'essayer de rattraper tous les points de sortie un par un.
     */
    save_campaign_params: false,
    save_referrer: false,
    // Replay is off by default and only started explicitly, scoped to the
    // onboarding (see useAnalyticsSession). When it runs, both inputs AND
    // rendered text are masked: customer PII (names, emails, addresses)
    // appears as DOM text on onboarding screens, not only in form fields, so
    // maskAllInputs alone would still leak it into the replay. The funnel
    // analysis needs where users drop, not the text itself.
    disable_session_recording: true,
    session_recording: {maskAllInputs: true, maskTextSelector: "*"},
  });
  initialized = true;
  captureEnabled = isCaptureMode(mode);
  /**
   * Purge le blob éventuellement persisté par une session ANTÉRIEURE à ce
   * fix (#920) : `save_campaign_params`/`save_referrer` à `false` ci-dessus
   * arrêtent toute NOUVELLE collecte, mais ne vident pas ce qui était déjà
   * en `localStorage` sur un navigateur ayant déjà chargé l'app — ce blob ne
   * s'efface jamais tout seul (pas de TTL, aucun `posthog.reset()` appelé
   * ailleurs dans le code) et continuerait de fuiter indéfiniment via
   * `POST /flags` sans cette purge. `unregister` est l'API publique dédiée
   * (`posthog.unregister('plan_type')` en exemple officiel) — pas un accès
   * à l'interne de la lib.
   */
  posthog.unregister("$initial_person_info");
  posthog.unregister("$initial_campaign_params");
  posthog.unregister("$initial_referrer_info");
  // Moderation opt-out — defense in depth on top of the per-call gates in
  // `analytics.service.ts` and `useFeatureFlag.ts`. Those gates only cover the
  // events we route through our wrappers; posthog-js emits internal captures
  // enabled by the remote config (notably `$web_vitals`) that bypass them.
  // Without this opt-out, an impersonation session would attribute those
  // internal events to the last identity persisted by the SDK on this browser
  // — a third-party account gets polluted (observed live 07/07, #625).
  // `opt_out_capturing` blocks capture only, not feature flag evaluation
  // (`/flags` still loads and `isFeatureEnabled` keeps resolving), so the
  // admin still sees the app exactly as the impersonated user does.
  if (authenticationApiService.isModeration()) {
    posthog.opt_out_capturing();
  }
}

export function isAnalyticsInitialized() {
  return initialized;
}

/**
 * Capture (events + session replay) gate, distinct from initialization. PostHog
 * is initialized everywhere so flags resolve, but only prod/dev emit: on
 * canary/latest the SDK is up (flags + `identify` for targeting) while
 * `analyticsService.track` and replay stay no-op, so test traffic never
 * pollutes the funnel.
 */
export function isCaptureEnabled() {
  return captureEnabled;
}

/** prod and dev are the only environments that emit events and record replays. */
function isCaptureMode(mode: ReturnType<typeof envService.getMode>): boolean {
  return mode === "prod" || mode === "dev";
}

/**
 * Maps each environment to its PostHog project key:
 *   - prod + canary → prod project (canary is a 1-user prod rehearsal),
 *   - dev  + latest → dev project (latest holds throwaway test accounts).
 * An empty key disables PostHog for the matching environment entirely.
 */
export function resolvePosthogKey(
  mode: ReturnType<typeof envService.getMode>,
  keys: {prod: string; dev: string}
): string | null {
  if (mode === "prod" || mode === "canary") {
    return keys.prod || null;
  }
  return keys.dev || null;
}
